import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { useGetList, useGetOne } from "react-admin";
import { parseUserIds } from "../text";
import { MAX_USER_IDS } from "../validate";

// Shared with T16 (role presets) — order matches the preset arrays there.
export const AUDIENCE_ROLES = ["admin", "owner", "creator", "member", "guest"];

const ROLE_LABELS = {
  admin: "Admin",
  owner: "Owner",
  creator: "Creator",
  member: "Member",
  guest: "Guest",
};

const MODE_OPTIONS = [
  { value: "all_users", label: "All users" },
  { value: "workspace", label: "Workspace" },
  { value: "user", label: "Specific users" },
];

// NOTIF-26 AC1 — exact arrays per preset. Applying a preset only sets
// audience_roles once; it does not keep enforcing the array afterward, so
// individual checkboxes stay freely editable (NOTIF-26 AC2).
const ROLE_PRESETS = [
  { label: "All roles", roles: ["admin", "owner", "creator", "member", "guest"] },
  { label: "Admins only", roles: ["admin", "owner", "creator"] },
  { label: "Members and up", roles: ["admin", "owner", "creator", "member"] },
];

// NOTIF-05 AC3 — exact wording, kept in its own node so it stays
// independently assertable from the case-sensitivity note below it.
const WORKSPACE_OPTIONAL_HELPER =
  "Leave empty to send to users with the selected roles in all workspaces.";

export default function AudienceStep({ state, dispatch, errors = {} }) {
  const [workspaceQuery, setWorkspaceQuery] = useState("");
  const hasPendingEditRef = useRef(false);

  const setField = (field, value) => dispatch({ type: "SET_FIELD", field, value });

  // Switching mode strips every other mode's fields from state (not just the
  // payload — A15 already keeps buildPayload mode-safe, but the controls
  // themselves must not keep showing stale values for an abandoned mode).
  const handleModeChange = (event) => {
    const value = event.target.value;
    setField("audience_type", value);
    setField("audience_roles", []);
    setField("user_ids", "");
    setField("workspace_id", "");
    setWorkspaceQuery("");
    // Relying only on workspace_id changing (which always happens here too)
    // to clear this would make the invariant implicit; reset it directly so
    // an abandoned search never carries a stale "pending edit" flag into a
    // freshly re-entered workspace mode.
    hasPendingEditRef.current = false;
  };

  // Dispatched as its own action (Composer.js reducer) rather than computed
  // here from the `state` prop: two toggles landing in the same tick would
  // otherwise both read the same pre-update snapshot, and the second SET_FIELD
  // would silently overwrite the first's result. The reducer always computes
  // `next` from its own live state argument, so dispatches queued in the same
  // tick still apply in order.
  const toggleRole = (role) => () => dispatch({ type: "TOGGLE_ROLE", role });

  const applyRolePreset = (roles) => () => setField("audience_roles", [...roles]);

  const trimmedQuery = workspaceQuery.trim();

  // A workspace id is a single opaque token (no whitespace); a name search
  // frequently contains spaces. We only attempt the id lookup for
  // whitespace-free input so pasting an id resolves the exact workspace,
  // while multi-word name typing stays purely a name search.
  const looksLikeId = trimmedQuery.length > 0 && !/\s/.test(trimmedQuery);

  // Query passed exactly as typed — the name match is server-side
  // case-sensitive (T1), so no case normalization happens here.
  const { data: nameMatches = [], isLoading: nameLoading } = useGetList(
    "workspaces",
    {
      pagination: { page: 1, perPage: 25 },
      filter: trimmedQuery ? { name: workspaceQuery } : {},
    },
    { enabled: trimmedQuery.length > 0 }
  );

  // Second lookup so an id can be pasted directly (the backend supports an
  // `id` filter — see the workspaces admin list). Operators used to paste an
  // id, get "No options" from the name-only search, and wrongly assume it took.
  const { data: idMatches = [], isLoading: idLoading } = useGetList(
    "workspaces",
    {
      pagination: { page: 1, perPage: 5 },
      filter: { id: trimmedQuery },
    },
    { enabled: looksLikeId }
  );

  const workspacesLoading = nameLoading || idLoading;

  // id matches first so an exact id paste surfaces at the top; deduped by id
  // so a workspace found by both name and id appears once.
  const workspaceOptions = useMemo(() => {
    const byId = new Map();
    [...idMatches, ...nameMatches].forEach((workspace) => {
      if (workspace?.id) {
        byId.set(workspace.id, workspace);
      }
    });
    return Array.from(byId.values());
  }, [idMatches, nameMatches]);

  const { data: selectedWorkspaceRecord } = useGetOne(
    "workspaces",
    { id: state.workspace_id },
    { enabled: Boolean(state.workspace_id) }
  );

  const selectedWorkspace = useMemo(
    () =>
      state.workspace_id
        ? { id: state.workspace_id, name: selectedWorkspaceRecord?.name || state.workspace_id }
        : null,
    [state.workspace_id, selectedWorkspaceRecord]
  );

  // Keeps the search box showing the resolved name of whatever workspace_id
  // is currently in state (e.g. after Back/forward), without clobbering an
  // in-progress search. `selectedWorkspaceRecord` resolves asynchronously
  // (useGetOne) and MUI's own Autocomplete additionally resyncs its input
  // text to match `value` on its own whenever that reference changes (even
  // mid-keystroke) — hasPendingEditRef tracks "the operator has typed since
  // the last programmatic sync" so both this effect and the onInputChange
  // handler below can decline to apply a resync while that's true. A real
  // workspace_id change (a fresh pick, or an explicit clear) always wins by
  // resetting the flag first.
  useEffect(() => {
    hasPendingEditRef.current = false;
  }, [state.workspace_id]);

  useEffect(() => {
    if (hasPendingEditRef.current) {
      return;
    }
    setWorkspaceQuery(selectedWorkspace ? selectedWorkspace.name : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.workspace_id, selectedWorkspaceRecord]);

  const userIdCount = parseUserIds(state.user_ids || "").length;
  const overUserIdLimit = userIdCount > MAX_USER_IDS;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <FormControl component="fieldset">
        <FormLabel component="legend">Audience mode</FormLabel>
        <RadioGroup value={state.audience_type} onChange={handleModeChange}>
          {MODE_OPTIONS.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={<Radio />}
              label={option.label}
            />
          ))}
        </RadioGroup>
      </FormControl>

      {state.audience_type === "workspace" && (
        <FormControl component="fieldset" error={Boolean(errors.audience_roles)}>
          <FormLabel component="legend">Roles</FormLabel>
          <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
            {ROLE_PRESETS.map((preset) => (
              <Button
                key={preset.label}
                size="small"
                variant="outlined"
                onClick={applyRolePreset(preset.roles)}
              >
                {preset.label}
              </Button>
            ))}
          </Box>
          <FormGroup>
            {AUDIENCE_ROLES.map((role) => (
              <FormControlLabel
                key={role}
                control={
                  <Checkbox
                    checked={(state.audience_roles || []).includes(role)}
                    onChange={toggleRole(role)}
                  />
                }
                label={ROLE_LABELS[role]}
              />
            ))}
          </FormGroup>
          {errors.audience_roles && <FormHelperText>{errors.audience_roles}</FormHelperText>}
        </FormControl>
      )}

      {state.audience_type === "workspace" && (
        <Box>
          <Autocomplete
            options={workspaceOptions}
            value={selectedWorkspace}
            loading={workspacesLoading}
            // Results are already filtered server-side (by name and by id); MUI's
            // default filter matches the typed text against the option *label*
            // (the name), which would hide an id-matched workspace whose name
            // does not contain the pasted id. Show exactly what the server returned.
            filterOptions={(options) => options}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            // Identical workspace names are common (e.g. three "Phonon X"), so
            // the plain name label alone leaves the operator guessing which row
            // is which. Each option renders its avatar (workspace image_url,
            // reusing the `?size=NxN` convention from workspaces.js) plus the id
            // in parentheses so the correct workspace is unambiguous at a glance.
            renderOption={(props, option) => {
              // MUI 5.15 injects `key` into `props`; spreading it would trip
              // React's "key spread" warning, so pull it out and set our own.
              const { key, ...optionProps } = props;
              return (
                <Box
                  component="li"
                  key={option.id}
                  {...optionProps}
                  sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                >
                  <Avatar
                    src={
                      option.image_url
                        ? `${option.image_url}?size=40x40`
                        : undefined
                    }
                    alt=""
                    variant="rounded"
                    sx={{ width: 28, height: 28, fontSize: 14 }}
                  >
                    {(option.name || "?").charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap>
                      {option.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                    >
                      ({option.id})
                    </Typography>
                  </Box>
                </Box>
              );
            }}
            inputValue={workspaceQuery}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === "input") {
                hasPendingEditRef.current = true;
              } else if (reason === "reset" && hasPendingEditRef.current) {
                // MUI resyncing the text to match `value` on its own — not a
                // real workspace_id change, so it must not clobber an edit
                // the operator made since the last programmatic sync.
                return;
              }
              setWorkspaceQuery(newInputValue);
            }}
            onChange={(event, newValue) => {
              hasPendingEditRef.current = false;
              setField("workspace_id", newValue ? newValue.id : "");
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Workspace (optional)"
                helperText={WORKSPACE_OPTIONAL_HELPER}
                onBlur={(event) => {
                  // Leaving the field without picking a new option or
                  // clearing it abandons the in-progress search — MUI's own
                  // blur handling (composed via params.onBlur) reverts the
                  // text to match the true `value`, and this flag must not
                  // be left blocking that revert.
                  hasPendingEditRef.current = false;
                  params.onBlur?.(event);
                }}
              />
            )}
          />
          <Typography variant="caption" color="text.secondary" display="block">
            Search by name (case-sensitive — enter the exact name) or paste a workspace ID.
          </Typography>
          {state.workspace_id && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              data-testid="selected-workspace"
              sx={{ mt: 0.5 }}
            >
              Selected: {selectedWorkspaceRecord?.name || "resolving name…"} (
              <Box component="span" sx={{ fontFamily: "monospace" }}>
                {state.workspace_id}
              </Box>
              )
            </Typography>
          )}
        </Box>
      )}

      {state.audience_type === "user" && (
        <Box>
          <TextField
            label="User IDs"
            value={state.user_ids}
            onChange={(event) => setField("user_ids", event.target.value)}
            error={Boolean(errors.user_ids)}
            helperText={errors.user_ids || "Paste ids separated by commas or new lines."}
            multiline
            minRows={4}
            fullWidth
          />
          <Typography variant="body2" color={overUserIdLimit ? "error" : "text.secondary"}>
            {userIdCount} / {MAX_USER_IDS}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
